/**
 * @App.tsx
 * @description Main application component that handles routing and authentication.
 */

import DeepInsightDetails from "@/pages/DeepInsightDetails";
import DeepInsights from "@/pages/DeepInsights";
import { useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { useAuthenticationContext } from "./store/authContext/authContext";
import NavBar from "./components/NavBar";
import Chat from "./components/chat/Chat";
import ReportDesignerV2 from "./pages/ReportDesignerV2";
import Attrition from "./pages/Attrition";
import Recruitment from "./pages/Recruitment";
import Workspace from "./pages/Workspace";
import Culture from "./pages/Culture";
import Chats from "./pages/Chats";
import QueryPage from "./pages/query";
import QueryAiPage from "./pages/queryai";
import Decks from "./pages/Decks";
import Deck from "./pages/Deck";
import {
  getCurrentUser,
  getDeepInsightsEnabled,
  pingServer,
  setAuthToken,
} from "./lib/serverComm";
import Login from "./components/Login";
import MainContentArea from "./components/MainContentArea";
import Reports from "./pages/Reports";
import { LOGIN_URL } from "./constants/api";
import Modal from "./components/ui/modal.tsx";
import StrategicInitiatives from "@/pages/StrategicInitiatives.tsx";
import SystemPrompts from "@/pages/SystemPrompts.tsx";
import { ParitaUserInfo } from "@/common/user.ts";
import Onboarding from "@/components/Onboarding.tsx";
import mixpanel from "./lib/mixpanel";

// Ping interval to keep session alive on the server
// Server is now the authoritative source for session expiration
const PING_INTERVAL = 5 * 60 * 1000; // 5 minutes

function App() {
  const { state: authContext, dispatch } = useAuthenticationContext();

  // Session monitoring effect
  useEffect(() => {
    // console.log("Session monitoring started");
    if (!authContext.userData) return;

    let pingIntervalId: NodeJS.Timeout | null = null;

    const checkSession = async () => {
      // Ping server to keep session alive and verify it's still valid
      // Server will return 401 if session has expired
      // console.log("Checking session");
      const isAlive = await pingServer();
      if (!isAlive) {
        authContext.handleLogout();
        return;
      }
    };

    // Start periodic ping to maintain session
    pingIntervalId = setInterval(checkSession, PING_INTERVAL);

    // Initial ping on mount
    checkSession();

    // Cleanup
    return () => {
      // console.log("Session monitoring cleanup");
      if (pingIntervalId) clearInterval(pingIntervalId);
    };
  }, [authContext]); // Include authContext to prevent stale closures

  // Auth initialization effect
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("authToken");

      if (!token) {
        if (window.location.pathname !== "/login") {
          window.location.href = LOGIN_URL;
        }
        return;
      }

      try {
        setAuthToken(token);
        // Load all user data in one call
        const user: ParitaUserInfo = await getCurrentUser();

        // look for the login flag in local storage.
        const isLogin = localStorage.getItem("userLogin") === "true";
        if (isLogin) {
          localStorage.removeItem("userLogin");
          const wkspc = user.workspaces.find((ws) => ws.is_current);
          const workspaceName = wkspc ? wkspc.name : "unknown workspace";

          mixpanel.identify(user.id);
          mixpanel.people.set_once({
            $name: `${user.first_name} ${user.last_name}`,
            $email: user.email,
          });
          mixpanel.setCurrentWorkspaceName(workspaceName);

          mixpanel.track("User Logged In", {});
        }
        dispatch({
          type: "setUserAction",
          payload: user,
        });

        const deepInsights = await getDeepInsightsEnabled();

        dispatch({
          type: "setDeepInsightsEnabledAction",
          payload: deepInsights.enabled,
        });
      } catch (error) {
        console.error("Error during auth initialization:", error);
      }
    };
    if (window.location.pathname !== "/login") {
      initializeAuth();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Router>
      <div className="flex flex-col h-screen font-lato">
        {authContext.userData && <NavBar />}
        <div className="flex flex-grow">
          <MainContentArea
            pinned={authContext.userData?.sidebar_settings.isPinned}
          >
            {authContext.userData && <Onboarding />}
            <Routes>
              <Route
                path="/login"
                element={
                  <Login
                    onAuthenticated={() => {
                      window.location.href = "/";
                    }}
                  />
                }
              />
              {authContext.userData ? (
                <>
                  <Route path="/" element={<Chat />} />
                  <Route path="/system-prompts" element={<SystemPrompts />} />
                  <Route path="/decks" element={<Decks />} />
                  <Route path="/decks/:id" element={<Deck />} />
                  <Route
                    path="/decks/:id/reports/:reportId"
                    element={<Deck />}
                  />
                  <Route path="/chat/:id" element={<Chat />} />
                  <Route path="/new-chat" element={<Chat />} />
                  <Route path="/chats" element={<Chats />} />
                  <Route path="/deep-insights" element={<DeepInsights />} />
                  <Route
                    path="/deep-insights/:insightId"
                    element={<DeepInsightDetails />}
                  />
                  <Route
                    path="/deep-insights/:insightId/:id"
                    element={<DeepInsightDetails />}
                  />
                  <Route
                    path="/report/:reportId"
                    element={<ReportDesignerV2 />}
                  />
                  <Route
                    path="/areport/:reportId"
                    element={<ReportDesignerV2 />}
                  />
                  <Route path="/new-report" element={<ReportDesignerV2 />} />
                  <Route path="/new-areport" element={<ReportDesignerV2 />} />
                  <Route path="/new-workspace" element={<Workspace />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/query" element={<QueryPage />} />
                  <Route path="/queryai" element={<QueryAiPage />} />
                  <Route
                    path="/strategic-initiatives"
                    element={<StrategicInitiatives />}
                  />
                  <Route path="/workspace/attrition" element={<Attrition />} />
                  <Route
                    path="/workspace/recruitment"
                    element={<Recruitment />}
                  />
                  <Route path="/workspace/culture" element={<Culture />} />
                  <Route path="/workspace/:id" element={<Workspace />} />
                </>
              ) : (
                <Route path="*" element={<div></div>} />
              )}
            </Routes>
          </MainContentArea>
        </div>
      </div>
      {authContext.modalState.isOpen && (
        <Modal type={authContext.modalState.type} />
      )}
    </Router>
  );
}

export default App;
